import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import MarketingLinks from '../components/MarketingLinks';
import { API_BASE_URL } from '../config/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

const ROBOT_PATTERNS = [
  'bot', 'crawler', 'spider', 'googlebot', 'google-inspectiontool',
  'facebookexternalhit', 'twitterbot', 'linkedinbot', 'pinterest',
  'slackbot', 'telegrambot', 'discordbot', 'whatsapp', 'Bytespider',
  'meta-externalagent', 'petalbot', 'AhrefsBot', 'SemrushBot',
  'MJ12bot', 'DotBot', 'SeznamBot', 'ccbot', 'DuckDuckBot',
  'bingbot', 'msnbot', 'yandexbot', 'baiduspider', 'yahoo'
];

function VisitorAnalysisPage() {
  const navigate = useNavigate();
  
  // State management
  const [allSessions, setAllSessions] = useState([]);
  const [allVisitors, setAllVisitors] = useState([]);
  const [currentView, setCurrentView] = useState('visitors');
  const [selectedSource, setSelectedSource] = useState(null);
  const [sortColumn, setSortColumn] = useState('firstSeen');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [visitorType, setVisitorType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadedStartDate, setLoadedStartDate] = useState(null);
  const [loadedEndDate, setLoadedEndDate] = useState(null);
  const [expandedVisitors, setExpandedVisitors] = useState(new Set());
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [modalSession, setModalSession] = useState(null);
  const [activityDetailsModal, setActivityDetailsModal] = useState(null); // { type: 'clickType'|'likedItem', eventType?, eventLabel?, designId?, designTitle? }

  useEffect(() => {
    document.title = 'Visitor Analysis - Design Store Admin';
    loadAllSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Utility functions
  const isRobot = useCallback((userAgent) => {
    if (!userAgent) return false;
    const ua = userAgent.toLowerCase();
    return ROBOT_PATTERNS.some(pattern => ua.includes(pattern.toLowerCase()));
  }, []);

  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  const formatTime = useCallback((dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }, []);

  const formatDuration = useCallback((ms) => {
    if (!ms || ms < 0) return '0s';
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  }, []);

  const getVisitorId = useCallback((ip, userAgent) => {
    return ip + '_' + btoa(userAgent).replace(/[^a-zA-Z0-9]/g, '').slice(0, 32);
  }, []);

  const calculatePageTime = useCallback((activities, sessionEndTime) => {
    const pageViews = activities.filter(a => a.eventType === 'page_view');
    pageViews.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    const sessionEnd = sessionEndTime ? new Date(sessionEndTime) : new Date();
    const pageStats = {};
    
    pageViews.forEach((view, j) => {
      const path = view.path;
      const nextView = pageViews[j + 1];
      const timeSpent = nextView 
        ? new Date(nextView.timestamp) - new Date(view.timestamp)
        : sessionEnd - new Date(view.timestamp);
      
      if (!pageStats[path]) {
        pageStats[path] = {
          path: path,
          firstVisit: view.timestamp,
          lastVisit: view.timestamp,
          totalTime: 0,
          visitCount: 0,
          referrers: []
        };
      }
      
      pageStats[path].totalTime += timeSpent;
      pageStats[path].visitCount++;
      pageStats[path].lastVisit = view.timestamp;
      if (view.referrer && !pageStats[path].referrers.includes(view.referrer)) {
        pageStats[path].referrers.push(view.referrer);
      }
    });
    
    return Object.values(pageStats);
  }, []);

  const aggregateVisitors = useCallback((sessions) => {
    const visitorMap = {};
    
    sessions.forEach(session => {
      const visitorId = getVisitorId(session.ipAddress, session.userAgent);
      
      if (!visitorMap[visitorId]) {
        visitorMap[visitorId] = {
          visitorId: visitorId,
          ipAddress: session.ipAddress,
          userAgent: session.userAgent,
          isRobot: session.isRobot,
          sessions: [],
          pages: {},
          firstSeen: session.startTime,
          lastSeen: session.lastActivity,
          totalActivities: 0
        };
      }
      
      const visitor = visitorMap[visitorId];
      visitor.sessions.push(session.sessionId);
      visitor.totalActivities += session.activities.length;
      visitor.firstSeen = visitor.firstSeen < session.startTime ? visitor.firstSeen : session.startTime;
      visitor.lastSeen = visitor.lastSeen > session.lastActivity ? visitor.lastSeen : session.lastActivity;
      
      const pageStats = calculatePageTime(session.activities, session.lastActivity);
      pageStats.forEach(page => {
        if (!visitor.pages[page.path]) {
          visitor.pages[page.path] = {
            path: page.path,
            totalTime: 0,
            visitCount: 0,
            referrers: []
          };
        }
        visitor.pages[page.path].totalTime += page.totalTime;
        visitor.pages[page.path].visitCount += page.visitCount;
        page.referrers.forEach(ref => {
          if (!visitor.pages[page.path].referrers.includes(ref)) {
            visitor.pages[page.path].referrers.push(ref);
          }
        });
      });
    });
    
    return Object.values(visitorMap).map(v => {
      const pages = Object.values(v.pages).map(p => ({
        ...p,
        avgTime: Math.round(p.totalTime / p.visitCount)
      }));
      const totalPageViews = pages.reduce((sum, p) => sum + p.visitCount, 0);
      const totalTimeSpent = pages.reduce((sum, p) => sum + p.totalTime, 0);
      
      return {
        ...v,
        pages: pages,
        uniquePages: pages.length,
        totalPageViews: totalPageViews,
        totalTimeSpent: totalTimeSpent
      };
    });
  }, [getVisitorId, calculatePageTime]);

  // Load sessions from API
  const loadAllSessions = useCallback(async () => {
    setError(null);
    setLoading(true);
    
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) {
      setError('Authentication required. Please log in.');
      setTimeout(() => navigate('/login'), 2000);
      setLoading(false);
      return;
    }

    try {
      // Get date range from state or default to last 30 days
      let startDateStr, endDateStr;
      if (startDate && endDate) {
        startDateStr = startDate;
        endDateStr = endDate;
      } else {
        const endDateObj = new Date();
        const startDateObj = new Date();
        startDateObj.setDate(startDateObj.getDate() - 30);
        startDateStr = startDateObj.toISOString().split('T')[0];
        endDateStr = endDateObj.toISOString().split('T')[0];
      }

      const apiUrl = `${API_BASE_URL}/admin/visitor-sessions/all?startDate=${startDateStr}&endDate=${endDateStr}&limit=10000`;
      
      const response = await authAxios.get(apiUrl);
      
      if (!response.data.success || !response.data.data) {
        setError('Invalid response from server');
        setLoading(false);
        return;
      }

      // Process sessions from API response
      const sessionsData = response.data.data;
      const processedSessions = sessionsData.map(session => {
        const isBot = isRobot(session.userAgent);
        const pageViewsArr = session.activities?.filter(a => a.eventType === 'page_view') || [];
        const uniquePaths = [...new Set(pageViewsArr.map(p => p.path))];
        const eventCount = session.activities?.filter(a => a.eventType !== 'page_view').length || 0;
        const dateStr = session.date || (session.startTime ? session.startTime.split('T')[0].replace(/-/g, '') : '');

        return {
          date: dateStr,
          sessionId: session.sessionId,
          ipAddress: session.ipAddress,
          userAgent: session.userAgent,
          httpReferer: session.httpReferer || '',
          isRobot: isBot,
          startTime: session.startTime,
          lastActivity: session.lastActivity,
          activities: session.activities || [],
          uniquePaths: uniquePaths,
          pageViews: pageViewsArr.length,
          events: eventCount
        };
      });

      setAllSessions(processedSessions);

      // Aggregate visitors
      const visitors = aggregateVisitors(processedSessions);
      setAllVisitors(visitors);

      // Set loaded date range
      if (response.data.startDate && response.data.endDate) {
        setLoadedStartDate(response.data.startDate);
        setLoadedEndDate(response.data.endDate);
        if (!startDate) setStartDate(response.data.startDate);
        if (!endDate) setEndDate(response.data.endDate);
      } else if (processedSessions.length > 0) {
        const dates = [...new Set(processedSessions.map(s => s.date))].sort();
        if (dates.length > 0) {
          let minDate = dates[0];
          let maxDate = dates[dates.length - 1];
          if (minDate.length === 8) {
            minDate = minDate.substring(0, 4) + '-' + minDate.substring(4, 6) + '-' + minDate.substring(6, 8);
          }
          if (maxDate.length === 8) {
            maxDate = maxDate.substring(0, 4) + '-' + maxDate.substring(4, 6) + '-' + maxDate.substring(6, 8);
          }
          setLoadedStartDate(minDate);
          setLoadedEndDate(maxDate);
          if (!startDate) setStartDate(minDate);
          if (!endDate) setEndDate(maxDate);
        }
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Authentication failed. Please log in again.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError('Error loading data: ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, isRobot, navigate, aggregateVisitors]);

  const matchesSearchTerm = useCallback((obj, term) => {
    const searchFields = [];
    if (obj.ipAddress) searchFields.push(obj.ipAddress);
    if (obj.userAgent) searchFields.push(obj.userAgent);
    if (obj.sessionId) searchFields.push(obj.sessionId);
    if (obj.firstSeen) searchFields.push(obj.firstSeen);
    if (obj.lastSeen) searchFields.push(obj.lastSeen);
    if (obj.pages) {
      obj.pages.forEach(page => searchFields.push(page.path));
    }
    if (obj.sessions) {
      obj.sessions.forEach(s => searchFields.push(s));
    }
    if (obj.uniquePaths) {
      obj.uniquePaths.forEach(p => searchFields.push(p));
    }
    if (obj.activities) {
      obj.activities.forEach(act => {
        if (act.path) searchFields.push(act.path);
        if (act.referrer) searchFields.push(act.referrer);
        if (act.eventType) searchFields.push(act.eventType);
      });
    }
    return searchFields.some(field => String(field).toLowerCase().includes(term.toLowerCase()));
  }, []);

  const categorizeReferrer = useCallback((referrer) => {
    if (!referrer || referrer === '/') return { category: 'Direct', displayName: 'Direct' };
    if (referrer.includes('facebook.com') || referrer.includes('fbclid=')) return { category: 'Facebook', displayName: 'Facebook' };
    if (referrer.includes('google') || referrer.includes('gclid=')) return { category: 'Google', displayName: 'Google' };
    if (referrer.includes('twitter.com') || referrer.includes('t.co')) return { category: 'Twitter', displayName: 'Twitter' };
    if (referrer.includes('linkedin.com')) return { category: 'LinkedIn', displayName: 'LinkedIn' };
    if (referrer.includes('pinterest')) return { category: 'Pinterest', displayName: 'Pinterest' };
    if (referrer.includes('bing.com') || referrer.includes('msn.com')) return { category: 'Bing', displayName: 'Bing' };
    if (referrer.includes('yahoo')) return { category: 'Yahoo', displayName: 'Yahoo' };
    if (referrer.includes('baidu')) return { category: 'Baidu', displayName: 'Baidu' };
    if (referrer.includes('instagram')) return { category: 'Instagram', displayName: 'Instagram' };
    if (referrer.includes('shop.doodlingjorge.com') || referrer.includes('doodlingjorge.com')) return { category: 'Internal', displayName: 'Internal' };
    const extractDomain = (url) => {
      try {
        if (!url.includes('://')) return url;
        let domain = url.split('/')[2];
        if (domain.startsWith('www.')) domain = domain.substring(4);
        return domain;
      } catch (e) { return url; }
    };
    return { category: 'Other', displayName: extractDomain(referrer) };
  }, []);

  // Normalize date to YYYYMMDD for comparison (handles both YYYY-MM-DD and YYYYMMDD from API)
  const toDateKey = useCallback((d) => (d ? String(d).replace(/-/g, '') : ''), []);

  // Filter functions
  const filterSessions = useCallback(() => {
    const startDateStr = toDateKey(startDate) || null;
    const endDateStr = toDateKey(endDate) || null;

    return allSessions.filter(session => {
      const sessionDateKey = toDateKey(session.date);
      if (startDateStr && sessionDateKey && sessionDateKey < startDateStr) return false;
      if (endDateStr && sessionDateKey && sessionDateKey > endDateStr) return false;
      if (visitorType === 'robot' && !session.isRobot) return false;
      if (visitorType === 'real' && session.isRobot) return false;

      if (selectedSource) {
        const hasSource = session.activities.some(act => {
          if (act.referrer && act.eventType === 'page_view') {
            const refInfo = categorizeReferrer(act.referrer);
            return refInfo.category === selectedSource;
          }
          return false;
        });
        if (!hasSource) return false;
      }

      if (searchTerm) {
        if (!matchesSearchTerm(session, searchTerm)) return false;
      }

      return true;
    });
  }, [allSessions, startDate, endDate, visitorType, selectedSource, searchTerm, toDateKey, categorizeReferrer, matchesSearchTerm]);

  const filterVisitors = useCallback(() => {
    const startDateObj = startDate ? new Date(startDate + 'T00:00:00') : null;
    const endDateObj = endDate ? new Date(endDate + 'T23:59:59.999') : null;

    return allVisitors.filter(visitor => {
      if (startDateObj) {
        const visitorLastSeen = new Date(visitor.lastSeen);
        if (visitorLastSeen < startDateObj) return false;
      }
      if (endDateObj) {
        const visitorFirstSeen = new Date(visitor.firstSeen);
        if (visitorFirstSeen > endDateObj) return false;
      }
      
      if (visitorType === 'robot' && !visitor.isRobot) return false;
      if (visitorType === 'real' && visitor.isRobot) return false;

      if (selectedSource) {
        let hasSource = false;
        for (const sessionId of visitor.sessions) {
          const session = allSessions.find(s => s.sessionId === sessionId);
          if (session) {
            hasSource = session.activities.some(act => {
              if (act.referrer && act.eventType === 'page_view') {
                const refInfo = categorizeReferrer(act.referrer);
                return refInfo.category === selectedSource;
              }
              return false;
            });
            if (hasSource) break;
          }
        }
        if (!hasSource) return false;
      }

      if (searchTerm) {
        if (!matchesSearchTerm(visitor, searchTerm)) return false;
      }

      return true;
    });
  }, [allVisitors, allSessions, startDate, endDate, visitorType, selectedSource, searchTerm, categorizeReferrer, matchesSearchTerm]);

  // Computed filtered data
  const filteredSessions = useMemo(() => filterSessions(), [filterSessions]);
  const filteredVisitors = useMemo(() => filterVisitors(), [filterVisitors]);

  // First/Last seen within selected date range (for display when date range is set)
  const visitorInRangeTimestamps = useMemo(() => {
    const result = {};
    const startDateObj = startDate ? new Date(startDate + 'T00:00:00') : null;
    const endDateObj = endDate ? new Date(endDate + 'T23:59:59.999') : null;
    const filteredSessionIds = new Set(filteredSessions.map(s => s.sessionId));

    filteredVisitors.forEach(visitor => {
      let firstInRange = null;
      let lastInRange = null;
      for (const sessionId of visitor.sessions) {
        if (!filteredSessionIds.has(sessionId)) continue;
        const session = filteredSessions.find(s => s.sessionId === sessionId);
        if (!session?.activities) continue;
        for (const act of session.activities) {
          const ts = new Date(act.timestamp);
          if (startDateObj && ts < startDateObj) continue;
          if (endDateObj && ts > endDateObj) continue;
          if (!firstInRange || ts < firstInRange) firstInRange = ts;
          if (!lastInRange || ts > lastInRange) lastInRange = ts;
        }
      }
      result[visitor.visitorId] = { firstSeenInRange: firstInRange, lastSeenInRange: lastInRange };
    });
    return result;
  }, [filteredVisitors, filteredSessions, startDate, endDate]);

  // Stats calculation
  const stats = useMemo(() => {
    const sessions = Array.isArray(filteredSessions) ? filteredSessions : [];
    const visitors = Array.isArray(filteredVisitors) ? filteredVisitors : [];
    // Get unique dates from sessions - handle both YYYYMMDD and YYYY-MM-DD formats
    const dateSet = new Set(sessions.map(s => {
      if (!s.date) return null;
      // If date is in YYYYMMDD format, convert to YYYY-MM-DD for consistency
      if (s.date.length === 8 && /^\d{8}$/.test(s.date)) {
        return s.date.substring(0, 4) + '-' + s.date.substring(4, 6) + '-' + s.date.substring(6, 8);
      }
      return s.date;
    }).filter(Boolean));

    const robots = visitors.filter(v => v.isRobot);
    const real = visitors.filter(v => !v.isRobot);
    const uniqueIPs = new Set(visitors.map(v => v.ipAddress));

    // Bounce rate, avg duration, engagement (design love + product click)
    let bounceRate = null;
    let avgDurationMs = null;
    let designLoveRate = null;
    let productClickRate = null;
    if (sessions.length > 0) {
      let bouncedCount = 0;
      let totalDurationMs = 0;
      let designLoveCount = 0;
      let productClickCount = 0;
      sessions.forEach(s => {
        const activities = s.activities || [];
        const pageViews = activities.filter(a => a.eventType === 'page_view').length;
        if (pageViews === 1) bouncedCount++;
        const start = new Date(s.startTime);
        const last = new Date(s.lastActivity || s.startTime);
        totalDurationMs += last - start;
        if (activities.some(a => a.eventType === 'design_love')) designLoveCount++;
        if (activities.some(a => a.eventType === 'product_click')) productClickCount++;
      });
      bounceRate = Math.round((bouncedCount / sessions.length) * 100);
      avgDurationMs = Math.round(totalDurationMs / sessions.length);
      designLoveRate = Math.round((designLoveCount / sessions.length) * 100);
      productClickRate = Math.round((productClickCount / sessions.length) * 100);
    }

    let sessionsLabel = 'Sessions';
    let realLabel = 'Real Sessions';
    if (currentView === 'visitors') {
      sessionsLabel = 'Visitors';
      realLabel = 'Real Visitors';
    } else if (currentView === 'clicktypes') {
      sessionsLabel = 'Total Events';
      realLabel = 'Event Types';
    } else if (currentView === 'popularpages') {
      sessionsLabel = 'Total Views';
      realLabel = 'Unique Pages';
    } else if (currentView === 'likeditems') {
      const likedItems = [];
      sessions.forEach(s => {
        (s.activities || []).forEach(act => {
          if (act.eventType === 'design_love' && act.metadata?.designId) {
            likedItems.push(act.metadata.designId);
          }
        });
      });
      const totalLikes = likedItems.length;
      const uniqueDesigns = new Set(likedItems).size;
      return {
        totalDays: dateSet.size,
        totalSessions: totalLikes,
        realVisitors: uniqueDesigns,
        robotVisitors: 0,
        uniqueVisitors: uniqueIPs.size,
        sessionsLabel: 'Total Likes',
        realLabel: 'Unique Designs',
        bounceRate,
        avgDurationMs,
        designLoveRate,
        productClickRate
      };
    }

    return {
      totalDays: dateSet.size,
      totalSessions: sessions.length,
      realVisitors: real.length,
      robotVisitors: robots.length,
      uniqueVisitors: uniqueIPs.size,
      sessionsLabel,
      realLabel,
      bounceRate,
      avgDurationMs,
      designLoveRate,
      productClickRate
    };
  }, [filteredSessions, filteredVisitors, currentView]);

  // Debug: Log when data changes (moved after filteredSessions/filteredVisitors are defined)
  useEffect(() => {
    console.log('VisitorAnalysis Debug:', {
      allSessions: allSessions.length,
      allVisitors: allVisitors.length,
      filteredSessions: filteredSessions.length,
      filteredVisitors: filteredVisitors.length,
      stats,
      loading,
      error
    });
  }, [allSessions.length, allVisitors.length, filteredSessions.length, filteredVisitors.length, stats, loading, error]);

  // Sort function
  const getSortFunction = useCallback((column, direction) => {
    const desc = direction === 'desc';
    return (a, b) => {
      let valA, valB;
      switch(column) {
        case 'firstSeen':
        case 'date':
          valA = new Date(a.firstSeenInRange || a.firstSeen || a.startTime || a.date);
          valB = new Date(b.firstSeenInRange || b.firstSeen || b.startTime || b.date);
          break;
        case 'ipAddress':
          valA = a.ipAddress || '';
          valB = b.ipAddress || '';
          break;
        case 'isRobot':
          valA = a.isRobot ? 1 : 0;
          valB = b.isRobot ? 1 : 0;
          break;
        case 'uniquePages':
          valA = a.uniquePages || 0;
          valB = b.uniquePages || 0;
          break;
        case 'totalTimeSpent':
          valA = a.totalTimeSpent || 0;
          valB = b.totalTimeSpent || 0;
          break;
        case 'sessions':
          valA = a.sessions ? a.sessions.length : 0;
          valB = b.sessions ? b.sessions.length : 0;
          break;
        case 'sessionId':
          valA = a.sessionId || '';
          valB = b.sessionId || '';
          break;
        case 'uniquePaths':
          valA = a.uniquePaths ? a.uniquePaths.length : 0;
          valB = b.uniquePaths ? b.uniquePaths.length : 0;
          break;
        default:
          return 0;
      }
      if (valA < valB) return desc ? 1 : -1;
      if (valA > valB) return desc ? -1 : 1;
      return 0;
    };
  }, []);

  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  }, [sortColumn]);

  const handleApplyFilters = useCallback(() => {
    // Check if we need to reload
    if (startDate && endDate && allSessions.length > 0 && loadedStartDate && loadedEndDate) {
      const requestedStart = new Date(startDate + 'T00:00:00');
      const requestedEnd = new Date(endDate + 'T23:59:59');
      const loadedStart = new Date(loadedStartDate + 'T00:00:00');
      const loadedEnd = new Date(loadedEndDate + 'T23:59:59');
      
      if (requestedStart.getTime() < loadedStart.getTime() || 
          requestedEnd.getTime() > loadedEnd.getTime()) {
        loadAllSessions();
        return;
      }
    } else if (allSessions.length === 0) {
      loadAllSessions();
      return;
    }
  }, [startDate, endDate, allSessions.length, loadedStartDate, loadedEndDate, loadAllSessions]);

  const handleClearFilters = useCallback(() => {
    setStartDate('');
    setEndDate('');
    setVisitorType('all');
    setSearchTerm('');
    setSelectedSource(null);
  }, []);

  const handleSourceFilter = useCallback((sourceName) => {
    setSelectedSource(sourceName);
    setCurrentView('visitors');
  }, []);

  const handleClearSourceFilter = useCallback(() => {
    setSelectedSource(null);
  }, []);

  const toggleVisitorDetails = useCallback((visitorId) => {
    setExpandedVisitors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(visitorId)) {
        newSet.delete(visitorId);
      } else {
        newSet.add(visitorId);
      }
      return newSet;
    });
  }, []);

  const toggleSessionDetails = useCallback((sessionId) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  }, []);

  const showSessionModal = useCallback((sessionId) => {
    const session = allSessions.find(s => s.sessionId === sessionId);
    if (session) {
      setModalSession(session);
    }
  }, [allSessions]);

  const closeModal = useCallback(() => {
    setModalSession(null);
  }, []);

  const closeActivityDetailsModal = useCallback(() => {
    setActivityDetailsModal(null);
  }, []);

  const getActivitiesForDetails = useCallback((sessions, filter) => {
    const items = [];
    sessions.forEach(session => {
      (session.activities || []).forEach(act => {
        if (filter.type === 'clickType' && act.eventType === filter.eventType) {
          items.push({ session, activity: act });
        } else if (filter.type === 'likedItem' && act.eventType === 'design_love' && act.metadata?.designId === filter.designId) {
          items.push({ session, activity: act });
        }
      });
    });
    return items.sort((a, b) => new Date(b.activity.timestamp) - new Date(a.activity.timestamp));
  }, []);

  const handleShowActivityDetails = useCallback((type, eventType, eventLabel, designId, designTitle) => {
    setActivityDetailsModal({ type, eventType, eventLabel, designId, designTitle });
  }, []);

  const handleShowSessionFromActivity = useCallback((sessionId) => {
    const session = allSessions.find(s => s.sessionId === sessionId);
    if (session) {
      setActivityDetailsModal(null);
      setModalSession(session);
    }
  }, [allSessions]);

  // Aggregate functions for different views
  const aggregateSources = useCallback((sessions) => {
    const sourceStats = {
      'Facebook': { count: 0, visitors: new Set(), examples: [] },
      'Google': { count: 0, visitors: new Set(), examples: [] },
      'Twitter': { count: 0, visitors: new Set(), examples: [] },
      'LinkedIn': { count: 0, visitors: new Set(), examples: [] },
      'Pinterest': { count: 0, visitors: new Set(), examples: [] },
      'Bing': { count: 0, visitors: new Set(), examples: [] },
      'Yahoo': { count: 0, visitors: new Set(), examples: [] },
      'Baidu': { count: 0, visitors: new Set(), examples: [] },
      'Instagram': { count: 0, visitors: new Set(), examples: [] },
      'Internal': { count: 0, visitors: new Set(), examples: [] },
      'Direct': { count: 0, visitors: new Set(), examples: [] },
      'Other': { count: 0, visitors: new Set(), examples: [] }
    };

    sessions.forEach(session => {
      const seenReferrers = {};
      let hasReferrer = false;

      session.activities.forEach(act => {
        if (act.referrer && act.eventType === 'page_view') {
          if (seenReferrers[act.referrer]) return;
          seenReferrers[act.referrer] = true;
          hasReferrer = true;

          const refInfo = categorizeReferrer(act.referrer);
          sourceStats[refInfo.category].count++;
          sourceStats[refInfo.category].visitors.add(session.ipAddress);
          if (sourceStats[refInfo.category].examples.length < 5) {
            sourceStats[refInfo.category].examples.push(act.referrer);
          }
        }
      });

      if (!hasReferrer) {
        sourceStats['Direct'].count++;
        sourceStats['Direct'].visitors.add(session.ipAddress);
      }
    });

    const extractDomain = (url) => {
      try {
        if (!url.includes('://')) return url;
        let domain = url.split('/')[2];
        if (domain.startsWith('www.')) domain = domain.substring(4);
        return domain;
      } catch (e) { return url; }
    };

    return Object.entries(sourceStats)
      .map(([name, stats]) => ({
        name,
        displayName: stats.examples.length > 0 && name === 'Other' ? extractDomain(stats.examples[0]) : name,
        count: stats.count,
        visitors: stats.visitors.size,
        examples: stats.examples
      }))
      .filter(s => s.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [categorizeReferrer]);

  const aggregateClickTypes = useCallback((sessions) => {
    const eventTypeLabels = {
      'page_view': 'Page View',
      'product_click': 'Product Click',
      'add_to_cart': 'Add to Cart',
      'checkout': 'Checkout',
      'purchase': 'Purchase',
      'exit': 'Exit',
      'format_click': 'Format Click',
      'tag_click': 'Tag Click',
      'category_click': 'Category Click',
      'cart_view': 'Cart View',
      'design_love': 'Design Love'
    };

    const eventTypes = {};
    let totalEvents = 0;

    sessions.forEach(session => {
      session.activities.forEach(act => {
        const eventType = act.eventType;
        if (!eventTypes[eventType]) {
          eventTypes[eventType] = {
            eventType: eventType,
            label: eventTypeLabels[eventType] || eventType,
            count: 0,
            uniqueSessions: new Set(),
            examples: []
          };
        }
        eventTypes[eventType].count++;
        eventTypes[eventType].uniqueSessions.add(session.sessionId);
        
        if (eventTypes[eventType].examples.length < 3 && act.path) {
          if (!eventTypes[eventType].examples.includes(act.path)) {
            eventTypes[eventType].examples.push(act.path);
          }
        }
        totalEvents++;
      });
    });

    return Object.values(eventTypes)
      .map(et => ({
        ...et,
        uniqueSessions: et.uniqueSessions.size,
        percentage: totalEvents > 0 ? Math.round((et.count / totalEvents) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);
  }, []);

  const aggregatePopularPages = useCallback((sessions) => {
    const pageStats = {};
    
    sessions.forEach(session => {
      session.activities.forEach(act => {
        if (!act.path) return;
        const path = act.path;
        
        if (!pageStats[path]) {
          pageStats[path] = {
            path: path,
            totalViews: 0,
            uniqueVisitors: new Set(),
            lastVisit: null,
            events: {}
          };
        }
        
        pageStats[path].totalViews++;
        pageStats[path].uniqueVisitors.add(session.ipAddress);
        
        if (!pageStats[path].lastVisit || act.timestamp > pageStats[path].lastVisit) {
          pageStats[path].lastVisit = act.timestamp;
        }
        
        if (!pageStats[path].events[act.eventType]) {
          pageStats[path].events[act.eventType] = 0;
        }
        pageStats[path].events[act.eventType]++;
      });
    });
    
    return Object.values(pageStats)
      .map(p => ({
        ...p,
        uniqueVisitors: p.uniqueVisitors.size
      }))
      .sort((a, b) => b.totalViews - a.totalViews);
  }, []);

  const aggregateLikedItems = useCallback((sessions) => {
    const likedMap = {};

    sessions.forEach(session => {
      (session.activities || []).forEach(act => {
        if (act.eventType !== 'design_love') return;
        const designId = act.metadata?.designId;
        if (!designId) return;

        const designTitle = act.metadata?.designTitle || designId;

        if (!likedMap[designId]) {
          likedMap[designId] = {
            designId,
            designTitle,
            count: 0,
            uniqueSessions: new Set()
          };
        }
        likedMap[designId].count++;
        likedMap[designId].uniqueSessions.add(session.sessionId);
      });
    });

    return Object.values(likedMap)
      .map(item => ({
        ...item,
        uniqueSessions: item.uniqueSessions.size
      }))
      .sort((a, b) => b.count - a.count);
  }, []);

  const get404Errors = useCallback((sessions) => {
    const errors = [];
    if (!sessions || !Array.isArray(sessions)) return errors;

    sessions.forEach(session => {
      const activities = [...session.activities].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      activities.forEach((act, j) => {
        if (act.path === '/note/404') {
          let previousPage = null;
          let previousReferrer = null;
          
          for (let k = j - 1; k >= 0; k--) {
            if (activities[k].path && activities[k].path !== '/note/404' && activities[k].eventType === 'page_view') {
              previousPage = activities[k].path;
              previousReferrer = activities[k].referrer || null;
              break;
            }
          }
          
          if (!previousPage && session.httpReferer) {
            previousReferrer = session.httpReferer;
          }
          
          const refInfo = previousReferrer ? categorizeReferrer(previousReferrer) : { category: 'Direct' };
          
          errors.push({
            date: session.date,
            sessionId: session.sessionId,
            timestamp: act.timestamp,
            source: refInfo.category,
            referrer: previousReferrer || 'Direct',
            previousPage: previousPage || '-',
            ipAddress: session.ipAddress,
            isRobot: session.isRobot
          });
        }
      });
    });
    
    return errors.sort((a, b) => {
      const aDate = a.date || '';
      const bDate = b.date || '';
      const dateCompare = String(bDate).localeCompare(String(aDate));
      if (dateCompare !== 0) return dateCompare;
      return new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
    });
  }, [categorizeReferrer]);

  // Render functions
  const renderVisitorsTable = useCallback(() => {
    const filteredSessionIds = new Set(filteredSessions.map(s => s.sessionId));
    const visitorsWithRangeData = filteredVisitors.map(v => {
      const tr = visitorInRangeTimestamps[v.visitorId];
      const sessionsInRange = v.sessions.filter(sid => filteredSessionIds.has(sid)).length;
      return {
        ...v,
        firstSeenInRange: tr?.firstSeenInRange,
        lastSeenInRange: tr?.lastSeenInRange,
        sessionsInRange
      };
    });
    const sortedVisitors = [...visitorsWithRangeData].sort(getSortFunction(sortColumn, sortDirection));

    if (sortedVisitors.length === 0) {
      return <div className="text-center py-16 text-gray-600">No visitors found for the selected date range</div>;
    }

    return (
      <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th onClick={() => handleSort('firstSeen')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 sticky top-0">
              First Seen {sortColumn === 'firstSeen' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('ipAddress')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              IP Address {sortColumn === 'ipAddress' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('isRobot')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Type {sortColumn === 'isRobot' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('uniquePages')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Pages {sortColumn === 'uniquePages' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('totalTimeSpent')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Total Time {sortColumn === 'totalTimeSpent' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('sessions')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Sessions {sortColumn === 'sessions' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedVisitors.map((visitor) => {
            const isExpanded = expandedVisitors.has(visitor.visitorId);
            const pagesList = visitor.pages.map(p => p.path).join(', ');
            
            return (
              <React.Fragment key={visitor.visitorId}>
                <tr className="hover:bg-gray-50 border-b border-gray-200">
                  <td className="px-4 py-3">{formatDate(visitor.firstSeenInRange || visitor.firstSeen)}</td>
                  <td className="px-4 py-3">{visitor.ipAddress}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      visitor.isRobot 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {visitor.isRobot ? 'Robot' : 'Real'}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate" title={pagesList}>
                    {visitor.uniquePages}
                  </td>
                  <td className="px-4 py-3">{formatDuration(visitor.totalTimeSpent)}</td>
                  <td className="px-4 py-3">{visitor.sessionsInRange ?? visitor.sessions.length}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleVisitorDetails(visitor.visitorId)}
                      className="text-blue-600 hover:underline"
                    >
                      {isExpanded ? 'Hide Details' : 'View Details'}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-gray-50">
                    <td colSpan="7" className="px-4 py-4">
                      <div className="bg-white p-4 rounded mb-4">
                        <strong>User Agent:</strong> {visitor.userAgent}
                        <div className="flex gap-5 mt-2 flex-wrap text-sm text-gray-600">
                          <span><strong>First Visit:</strong> {formatDate(visitor.firstSeenInRange || visitor.firstSeen)} {formatTime(visitor.firstSeenInRange || visitor.firstSeen)}</span>
                          <span><strong>Last Activity:</strong> {formatDate(visitor.lastSeenInRange || visitor.lastSeen)} {formatTime(visitor.lastSeenInRange || visitor.lastSeen)}</span>
                          <span><strong>Total Page Views:</strong> {visitor.totalPageViews}</span>
                        </div>
                      </div>
                      <h4 className="font-semibold mb-2">Page Activity</h4>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-3 py-2 text-left">Page</th>
                            <th className="px-3 py-2 text-left">Visits</th>
                            <th className="px-3 py-2 text-left">Total Time</th>
                            <th className="px-3 py-2 text-left">Avg Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visitor.pages.map((page, i) => (
                            <tr key={i} className="border-b">
                              <td className="px-3 py-2 max-w-xs truncate" title={page.path}>{page.path}</td>
                              <td className="px-3 py-2">{page.visitCount}</td>
                              <td className="px-3 py-2">
                                <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                                  {formatDuration(page.totalTime)}
                                </span>
                              </td>
                              <td className="px-3 py-2">
                                <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                                  {formatDuration(page.avgTime)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <h4 className="font-semibold mt-4 mb-2">Session Timeline</h4>
                      <div className="pl-5 border-l-2 border-gray-300">
                        {(() => {
                          const filteredSessionIds = new Set(filteredSessions.map(s => s.sessionId));
                          return visitor.sessions
                            .filter(sessionId => filteredSessionIds.has(sessionId))
                            .map((sessionId, s) => {
                          const session = allSessions.find(s => s.sessionId === sessionId);
                          if (!session) return null;
                          
                          return (
                            <div key={s} className="relative pl-5 pb-4">
                              <div className="absolute left-[-6px] top-2 w-3 h-3 bg-blue-600 rounded-full"></div>
                              <div className="text-xs text-gray-600">
                                {formatDate(session.startTime)} {formatTime(session.startTime)}
                              </div>
                              <div className="font-medium">Session started</div>
                              <div className="mt-2 ml-3 space-y-1">
                                {session.activities.slice(0, 10).map((act, a) => {
                                  const pathLink = act.path ? (
                                    <a href={`https://shop.doodlingjorge.com${act.path}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                      {act.path}
                                    </a>
                                  ) : '-';
                                  return (
                                    <div key={a} className="bg-white p-2 rounded border-l-3 border-blue-500 text-xs">
                                      <span className="text-gray-600">{formatTime(act.timestamp)}</span> - <span className="font-medium">{pathLink}</span> - <span className="text-blue-600">{act.eventType}</span>
                                    </div>
                                  );
                                })}
                                {session.activities.length > 10 && (
                                  <div className="text-xs text-gray-500 italic">... and {session.activities.length - 10} more activities</div>
                                )}
                              </div>
                            </div>
                          );
                        });
                        })()}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }, [filteredVisitors, filteredSessions, visitorInRangeTimestamps, sortColumn, sortDirection, expandedVisitors, allSessions, formatDate, formatTime, formatDuration, getSortFunction, handleSort, toggleVisitorDetails]);

  const renderSessionsTable = useCallback(() => {
    const sortedSessions = [...filteredSessions].sort(getSortFunction(sortColumn, sortDirection));
    
    if (sortedSessions.length === 0) {
      return <div className="text-center py-16 text-gray-600">No sessions found for the selected date range</div>;
    }

    return (
      <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th onClick={() => handleSort('date')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Date {sortColumn === 'date' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('sessionId')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Session ID {sortColumn === 'sessionId' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('ipAddress')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              IP Address {sortColumn === 'ipAddress' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('isRobot')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Type {sortColumn === 'isRobot' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('uniquePaths')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Pages Visited {sortColumn === 'uniquePaths' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedSessions.map((session) => {
            const isExpanded = expandedSessions.has(session.sessionId);
            const pathsStr = session.uniquePaths.join(', ') || '-';
            
            return (
              <React.Fragment key={session.sessionId}>
                <tr className="hover:bg-gray-50 border-b border-gray-200">
                  <td className="px-4 py-3">{formatDate(session.startTime)}</td>
                  <td className="px-4 py-3 max-w-xs truncate" title={session.sessionId}>
                    {session.sessionId.substring(0, 20)}...
                  </td>
                  <td className="px-4 py-3">{session.ipAddress}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      session.isRobot 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {session.isRobot ? 'Robot' : 'Real'}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-xs truncate" title={pathsStr}>
                    {pathsStr}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleSessionDetails(session.sessionId)}
                      className="text-blue-600 hover:underline"
                    >
                      {isExpanded ? 'Hide Path' : 'View Path'}
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="bg-gray-50">
                    <td colSpan="6" className="px-4 py-4">
                      <div className="text-sm text-gray-600 mb-3">
                        <strong>User Agent:</strong> {session.userAgent}<br/>
                        <strong>Session:</strong> {formatDate(session.startTime)} to {formatDate(session.lastActivity)}<br/>
                        <strong>Page Views:</strong> {session.pageViews} | <strong>Other Actions:</strong> {session.events}<br/>
                        <strong>Unique Pages:</strong> {session.uniquePaths.length}
                      </div>
                      <div className="space-y-1">
                        {session.activities.map((act, i) => {
                          const pathLink = act.path ? (
                            <a href={`https://shop.doodlingjorge.com${act.path}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              {act.path}
                            </a>
                          ) : '-';
                          const metaStr = act.metadata && Object.keys(act.metadata).length ? JSON.stringify(act.metadata) : '';
                          return (
                            <div key={i} className="bg-white p-2 rounded border-l-3 border-blue-500 text-xs">
                              <span className="text-gray-600">{formatTime(act.timestamp)}</span> - <span className="font-medium">{pathLink}</span> - <span className="text-blue-600">{act.eventType}</span>
                              {metaStr && <span className="ml-2 text-gray-500">{metaStr}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }, [filteredSessions, sortColumn, sortDirection, expandedSessions, formatDate, formatTime, getSortFunction, handleSort, toggleSessionDetails]);

  const renderSourcesTable = useCallback(() => {
    const sources = aggregateSources(filteredSessions);
    
    if (sources.length === 0) {
      return <div className="text-center py-16 text-gray-600">No source data found for the selected date range</div>;
    }

    const getSourceBadgeClass = (name) => {
      if (name === 'Facebook') return 'bg-blue-600 text-white';
      if (name === 'Google') return 'bg-blue-500 text-white';
      if (name === 'Twitter') return 'bg-blue-400 text-white';
      if (name === 'Direct') return 'bg-green-500 text-white';
      return 'bg-gray-600 text-white';
    };

    return (
      <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Source</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Visits</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Unique Visitors</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Example Referrers</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((source, i) => {
            const isSelected = selectedSource === source.name;
            return (
              <tr key={i} className="hover:bg-gray-50 border-b border-gray-200">
                <td className="px-4 py-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSourceBadgeClass(source.name)}`}>
                    {source.displayName}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    onClick={() => handleSourceFilter(source.name)}
                    className={`cursor-pointer ${isSelected ? 'bg-blue-600 text-white px-2 py-1 rounded' : 'text-blue-600 underline'}`}
                  >
                    {source.count}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    onClick={() => handleSourceFilter(source.name)}
                    className={`cursor-pointer ${isSelected ? 'bg-blue-600 text-white px-2 py-1 rounded' : 'text-blue-600 underline'}`}
                  >
                    {source.visitors}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {source.name === 'Direct' ? (
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSourceBadgeClass(source.name)}`}>
                      Direct access (no referrer)
                    </span>
                  ) : (
                    source.examples.map((ex, j) => {
                      const displayEx = ex.length > 50 ? ex.substring(0, 47) + '...' : ex;
                      return (
                        <span key={j} className={`px-3 py-1 rounded-full text-xs font-medium mr-1 mb-1 inline-block ${getSourceBadgeClass(source.name)}`} title={ex}>
                          {displayEx}
                        </span>
                      );
                    })
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }, [filteredSessions, selectedSource, aggregateSources, handleSourceFilter]);

  const renderClickTypesTable = useCallback(() => {
    const clickTypes = aggregateClickTypes(filteredSessions);
    
    if (clickTypes.length === 0) {
      return <div className="text-center py-16 text-gray-600">No click data found for the selected date range</div>;
    }

    const totalCount = clickTypes.reduce((sum, ct) => sum + ct.count, 0);

    const getEventBadgeClass = (eventType) => {
      const classes = {
        'page_view': 'bg-blue-100 text-blue-800',
        'product_click': 'bg-orange-100 text-orange-800',
        'add_to_cart': 'bg-green-100 text-green-800',
        'checkout': 'bg-pink-100 text-pink-800',
        'purchase': 'bg-purple-100 text-purple-800',
        'exit': 'bg-red-100 text-red-800',
        'format_click': 'bg-cyan-100 text-cyan-800',
        'tag_click': 'bg-yellow-100 text-yellow-800',
        'category_click': 'bg-lime-100 text-lime-800',
        'cart_view': 'bg-amber-100 text-amber-800',
        'design_love': 'bg-rose-100 text-rose-800'
      };
      return classes[eventType] || 'bg-gray-100 text-gray-800';
    };

    return (
      <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Event Type</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Count</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">% of Total</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Unique Sessions</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Example Pages</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-gray-100 font-semibold">
            <td className="px-4 py-3">Total</td>
            <td className="px-4 py-3">{totalCount}</td>
            <td className="px-4 py-3">100%</td>
            <td className="px-4 py-3">-</td>
            <td className="px-4 py-3">-</td>
          </tr>
          {clickTypes.map((ct, i) => (
            <tr key={i} className="hover:bg-gray-50 border-b border-gray-200">
              <td className="px-4 py-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getEventBadgeClass(ct.eventType)}`}>
                  {ct.label}
                </span>
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => handleShowActivityDetails('clickType', ct.eventType, ct.label)}
                  className="font-semibold text-blue-600 hover:underline text-left"
                >
                  {ct.count}
                </button>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600" style={{ width: `${ct.percentage}%` }}></div>
                  </div>
                  <span>{ct.percentage}%</span>
                </div>
              </td>
              <td className="px-4 py-3">{ct.uniqueSessions}</td>
              <td className="px-4 py-3">
                {ct.examples.length > 0 ? (
                  ct.examples.map((ex, j) => {
                    const displayEx = ex.length > 40 ? ex.substring(0, 37) + '...' : ex;
                    return (
                      <div key={j} className="text-xs max-w-xs truncate" title={ex}>
                        {displayEx}
                      </div>
                    );
                  })
                ) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }, [filteredSessions, aggregateClickTypes, handleShowActivityDetails]);

  const renderPopularPagesTable = useCallback(() => {
    const pages = aggregatePopularPages(filteredSessions);
    
    if (pages.length === 0) {
      return <div className="text-center py-16 text-gray-600">No page data found for the selected date range</div>;
    }

    const eventLabels = {
      'page_view': 'Views',
      'product_click': 'Product',
      'add_to_cart': 'Cart',
      'checkout': 'Checkout',
      'purchase': 'Purchase',
      'exit': 'Exit',
      'format_click': 'Format',
      'tag_click': 'Tag',
      'category_click': 'Cat',
      'cart_view': 'CartView'
    };

    const getEventBadgeClass = (eventType) => {
      const classes = {
        'page_view': 'bg-blue-100 text-blue-800',
        'product_click': 'bg-orange-100 text-orange-800',
        'add_to_cart': 'bg-green-100 text-green-800',
        'checkout': 'bg-pink-100 text-pink-800',
        'purchase': 'bg-purple-100 text-purple-800',
        'exit': 'bg-red-100 text-red-800',
        'format_click': 'bg-cyan-100 text-cyan-800',
        'tag_click': 'bg-yellow-100 text-yellow-800',
        'category_click': 'bg-lime-100 text-lime-800',
        'cart_view': 'bg-amber-100 text-amber-800'
      };
      return classes[eventType] || 'bg-gray-100 text-gray-800';
    };

    return (
      <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th onClick={() => handleSort('rank')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              # {sortColumn === 'rank' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('path')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Page {sortColumn === 'path' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('totalViews')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Total Views {sortColumn === 'totalViews' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('uniqueVisitors')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Unique Visitors {sortColumn === 'uniqueVisitors' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Click Breakdown</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Last Visit</th>
          </tr>
        </thead>
        <tbody>
          {pages.map((page, i) => {
            const pathDisplay = page.path.length > 50 ? page.path.substring(0, 47) + '...' : page.path;
            const breakdownHtml = Object.entries(page.events).map(([eventType, count]) => {
              const label = eventLabels[eventType] || eventType;
              return (
                <span key={eventType} className={`px-2 py-1 rounded-full text-xs font-medium mr-1 ${getEventBadgeClass(eventType)}`} title={eventType}>
                  {label}:{count}
                </span>
              );
            });

            return (
              <tr key={i} className="hover:bg-gray-50 border-b border-gray-200">
                <td className="px-4 py-3 font-bold text-blue-600">{i + 1}</td>
                <td className="px-4 py-3 max-w-xs truncate" title={page.path}>
                  <a href={`https://shop.doodlingjorge.com${page.path}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {pathDisplay}
                  </a>
                </td>
                <td className="px-4 py-3 font-semibold">{page.totalViews}</td>
                <td className="px-4 py-3">{page.uniqueVisitors}</td>
                <td className="px-4 py-3">{breakdownHtml}</td>
                <td className="px-4 py-3">{page.lastVisit ? formatDate(page.lastVisit) : '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }, [filteredSessions, sortColumn, sortDirection, formatDate, handleSort, aggregatePopularPages]);

  const renderLikedItemsTable = useCallback(() => {
    const likedItems = aggregateLikedItems(filteredSessions);

    if (likedItems.length === 0) {
      return <div className="text-center py-16 text-gray-600">No liked items in the selected date range. Likes are tracked when visitors click the heart on design detail pages.</div>;
    }

    const totalLikes = likedItems.reduce((sum, item) => sum + item.count, 0);

    return (
      <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left font-semibold text-gray-900">#</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Design</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Design ID</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Likes</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">% of Total</th>
          </tr>
        </thead>
        <tbody>
          {likedItems.map((item, i) => {
            const pct = totalLikes > 0 ? Math.round((item.count / totalLikes) * 100) : 0;
            return (
              <tr key={item.designId} className="hover:bg-gray-50 border-b border-gray-200">
                <td className="px-4 py-3 font-bold text-blue-600">{i + 1}</td>
                <td className="px-4 py-3">
                  <a href={`/design/${item.designId}`} className="text-blue-600 hover:underline">
                    {item.designTitle}
                  </a>
                </td>
                <td className="px-4 py-3 text-gray-600 font-mono text-sm">{item.designId}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleShowActivityDetails('likedItem', null, null, item.designId, item.designTitle)}
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    {item.count}
                  </button>
                </td>
                <td className="px-4 py-3">{pct}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }, [filteredSessions, aggregateLikedItems, handleShowActivityDetails]);

  const render404AnalysisTable = useCallback(() => {
    const errors = get404Errors(filteredSessions);

    if (!errors || errors.length === 0) {
      return <div className="text-center py-16 text-gray-600">No 404 errors found for the selected filters</div>;
    }

    const getSourceBadgeClass = (source) => {
      if (source === 'Facebook') return 'bg-blue-600 text-white';
      if (source === 'Google') return 'bg-blue-500 text-white';
      if (source === 'Twitter') return 'bg-blue-400 text-white';
      if (source === 'Direct') return 'bg-green-500 text-white';
      return 'bg-gray-600 text-white';
    };

    return (
      <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Date</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Session ID</th>
            <th onClick={() => handleSort('time')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Time {sortColumn === 'time' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th onClick={() => handleSort('source')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              Source {sortColumn === 'source' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">Referrer Page</th>
            <th className="px-4 py-3 text-left font-semibold text-gray-900">404 Page</th>
            <th onClick={() => handleSort('ipAddress')} className="px-4 py-3 text-left font-semibold text-gray-900 cursor-pointer hover:bg-gray-100">
              IP Address {sortColumn === 'ipAddress' && (sortDirection === 'asc' ? '▲' : '▼')}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-gray-100 font-semibold">
            <td colSpan="7" className="px-4 py-3">
              Total 404 Errors: {errors.length}
            </td>
          </tr>
          {errors.map((error, idx) => {
            const dateStr = formatDate(error.timestamp);
            const timeStr = formatTime(error.timestamp);
            const referrerDisplay = error.referrer && error.referrer.length > 50 
              ? error.referrer.substring(0, 47) + '...' 
              : error.referrer;
            const sessionDisplay = error.sessionId.length > 20 
              ? error.sessionId.substring(0, 20) + '...' 
              : error.sessionId;

            return (
              <tr key={idx} className="hover:bg-gray-50 border-b border-gray-200">
                <td className="px-4 py-3">{dateStr}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => showSessionModal(error.sessionId)}
                    className="text-blue-600 hover:underline"
                    title={error.sessionId}
                  >
                    {sessionDisplay}
                  </button>
                </td>
                <td className="px-4 py-3">{timeStr}</td>
                <td className="px-4 py-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSourceBadgeClass(error.source)}`}>
                    {error.source}
                  </span>
                </td>
                <td className="px-4 py-3 max-w-xs truncate" title={error.referrer || ''}>
                  {referrerDisplay}
                </td>
                <td className="px-4 py-3 max-w-xs truncate" title={error.previousPage}>
                  {error.previousPage}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium mr-2 ${
                    error.isRobot 
                      ? 'bg-red-100 text-red-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {error.isRobot ? 'Robot' : 'Real'}
                  </span>
                  {error.ipAddress}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }, [get404Errors, filteredSessions, sortColumn, sortDirection, formatDate, formatTime, handleSort, showSessionModal]);

  const renderSessionModal = useCallback((session) => {
    const badgeClass = session.isRobot ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800';
    const badgeText = session.isRobot ? 'Robot' : 'Real';

    return (
      <>
        <div className="mb-4">
          <h4 className="font-semibold mb-2">Session Info</h4>
          <p><strong>Session ID:</strong> {session.sessionId}</p>
          <p><strong>Date:</strong> {formatDate(session.startTime)}</p>
          <p><strong>Start Time:</strong> {formatDate(session.startTime)} {formatTime(session.startTime)}</p>
          <p><strong>Last Activity:</strong> {formatDate(session.lastActivity)} {formatTime(session.lastActivity)}</p>
          <p><strong>IP Address:</strong> {session.ipAddress}</p>
          <p><strong>Type:</strong> <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>{badgeText}</span></p>
          <p><strong>HTTP Referer:</strong> {session.httpReferer || '-'}</p>
          <p><strong>User Agent:</strong> {session.userAgent}</p>
        </div>
        <div>
          <h4 className="font-semibold mb-2">Activities ({session.activities.length})</h4>
          <div className="space-y-1">
            {session.activities.map((act, i) => {
              const pathLink = act.path ? (
                <a href={`https://shop.doodlingjorge.com${act.path}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  {act.path}
                </a>
              ) : '-';
              const referrerDisplay = act.referrer || '-';
              return (
                <div key={i} className="bg-gray-50 p-2 rounded border-l-3 border-blue-500 text-sm">
                  <strong>{formatTime(act.timestamp)}</strong> - {act.eventType}<br/>
                  <span className="text-gray-600">Page: {pathLink}</span><br/>
                  <span className="text-gray-600">Referrer: {referrerDisplay}</span>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }, [formatDate, formatTime]);

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <AdminHeader />
        <AdminTabs />
        <MarketingLinks />
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="mb-4">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Visitor Session Analytics</h1>
              <p className="text-sm text-gray-500">v0.0.2</p>
            </div>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {/* View Tabs */}
            <div className="flex gap-0 mb-5 border-b-2 border-gray-300">
              {['visitors', 'sessions', 'sources', 'clicktypes', 'popularpages', 'likeditems', '404analysis'].map(view => (
                <button
                  key={view}
                  onClick={() => setCurrentView(view)}
                  className={`px-6 py-3 text-sm font-medium border-b-3 transition-colors ${
                    currentView === view
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-600 border-transparent hover:text-blue-600'
                  }`}
                  style={{ marginBottom: '-2px' }}
                >
                  {view === 'visitors' ? 'Visitors View' :
                   view === 'sessions' ? 'Sessions View' :
                   view === 'sources' ? 'Sources View' :
                   view === 'clicktypes' ? 'Click Types' :
                   view === 'popularpages' ? 'Popular Pages' :
                   view === 'likeditems' ? 'Liked Items' :
                   '404 Analysis'}
                </button>
              ))}
            </div>

            {/* Controls */}
            <div className="flex gap-5 mb-5 flex-wrap items-center">
              <div className="flex items-center gap-2">
                <label className="font-medium text-gray-700">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-medium text-gray-700">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-medium text-gray-700">Type:</label>
                <select
                  value={visitorType}
                  onChange={(e) => setVisitorType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm"
                >
                  <option value="all">All Visitors</option>
                  <option value="real">Real Visitors</option>
                  <option value="robot">Robots Only</option>
                </select>
              </div>
              <button
                onClick={handleApplyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
              >
                Clear Filters
              </button>
              {selectedSource && (currentView === 'visitors' || currentView === 'sessions') && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full text-sm">
                  <span>Source: <span className="font-semibold text-blue-600">{selectedSource}</span></span>
                  <button
                    onClick={handleClearSourceFilter}
                    className="bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-red-700"
                    title="Clear source filter"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-5">
              <div className="bg-white p-5 rounded-lg shadow-sm text-center border-l-4 border-blue-500">
                <h3 className="text-3xl font-bold text-blue-600 mb-1">{stats.totalDays}</h3>
                <p className="text-sm text-gray-600">Days</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm text-center border-l-4 border-blue-500">
                <h3 className="text-3xl font-bold text-blue-600 mb-1">{stats.totalSessions}</h3>
                <p className="text-sm text-gray-600">{stats.sessionsLabel}</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm text-center border-l-4 border-green-500">
                <h3 className="text-3xl font-bold text-blue-600 mb-1">{stats.realVisitors}</h3>
                <p className="text-sm text-gray-600">{stats.realLabel}</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm text-center border-l-4 border-red-500">
                <h3 className="text-3xl font-bold text-blue-600 mb-1">{stats.robotVisitors}</h3>
                <p className="text-sm text-gray-600">Robots</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm text-center border-l-4 border-blue-500">
                <h3 className="text-3xl font-bold text-blue-600 mb-1">{stats.uniqueVisitors}</h3>
                <p className="text-sm text-gray-600">Unique IPs</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm text-center border-l-4 border-amber-500">
                <div className="space-y-0.5">
                  <div className="text-2xl font-bold text-amber-600">{stats.bounceRate != null ? `${stats.bounceRate}%` : '-'}</div>
                  <div className="text-2xl font-bold text-amber-600">{stats.avgDurationMs != null ? formatDuration(stats.avgDurationMs) : '-'}</div>
                </div>
                <p className="text-sm text-gray-600 mt-1">Bounce Rate / Avg Time</p>
              </div>
              <div className="bg-white p-5 rounded-lg shadow-sm text-center border-l-4 border-emerald-500">
                <div className="space-y-0.5">
                  <div className="text-2xl font-bold text-emerald-600">{stats.designLoveRate != null ? `♥${stats.designLoveRate}%` : '-'}</div>
                  <div className="text-2xl font-bold text-emerald-600">{stats.productClickRate != null ? `${stats.productClickRate}%` : '-'}</div>
                </div>
                <p className="text-sm text-gray-600 mt-1">Engagement (Love / Click)</p>
              </div>
            </div>

            {/* Search */}
            <div className="flex items-center gap-2 mb-4">
              <label className="font-medium text-gray-700">Search:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search all records..."
                className="px-3 py-2 border border-gray-300 rounded text-sm w-64 focus:outline-none focus:border-blue-500"
              />
            </div>

            {/* Table Content */}
            {loading ? (
              <div className="text-center py-10 text-gray-600">Loading data...</div>
            ) : (
              <div className="overflow-x-auto">
                {currentView === 'visitors' && renderVisitorsTable()}
                {currentView === 'sessions' && renderSessionsTable()}
                {currentView === 'sources' && renderSourcesTable()}
                {currentView === 'clicktypes' && renderClickTypesTable()}
                {currentView === 'popularpages' && renderPopularPagesTable()}
                {currentView === 'likeditems' && renderLikedItemsTable()}
                {currentView === '404analysis' && render404AnalysisTable()}
              </div>
            )}

            {/* Modal */}
            {modalSession && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" onClick={closeModal}>
                <div className="bg-white rounded-lg p-6 w-11/12 max-w-4xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-between items-center mb-4 border-b pb-3">
                    <h3 className="text-xl font-bold">Session Details</h3>
                    <button onClick={closeModal} className="text-2xl text-gray-600 hover:text-gray-900">×</button>
                  </div>
                  {renderSessionModal(modalSession)}
                </div>
              </div>
            )}

            {/* Activity Details Modal (when count clicked in Click Types or Liked Items) */}
            {activityDetailsModal && (() => {
              const activities = getActivitiesForDetails(filteredSessions, activityDetailsModal);
              const title = activityDetailsModal.type === 'clickType'
                ? `${activityDetailsModal.eventLabel || activityDetailsModal.eventType} - ${activities.length} events`
                : `Likes for "${activityDetailsModal.designTitle || activityDetailsModal.designId}" - ${activities.length} likes`;
              return (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" onClick={closeActivityDetailsModal}>
                  <div className="bg-white rounded-lg p-6 w-11/12 max-w-4xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-4 border-b pb-3">
                      <h3 className="text-xl font-bold">Click Details: {title}</h3>
                      <button onClick={closeActivityDetailsModal} className="text-2xl text-gray-600 hover:text-gray-900">×</button>
                    </div>
                    {activities.length === 0 ? (
                      <p className="text-gray-600">No activities found.</p>
                    ) : (
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left font-semibold">Date</th>
                            <th className="px-3 py-2 text-left font-semibold">Time</th>
                            <th className="px-3 py-2 text-left font-semibold">Session ID</th>
                            <th className="px-3 py-2 text-left font-semibold">Path</th>
                            <th className="px-3 py-2 text-left font-semibold">Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activities.map(({ session, activity }, i) => (
                            <tr key={i} className="hover:bg-gray-50 border-b border-gray-200">
                              <td className="px-3 py-2">{formatDate(activity.timestamp)}</td>
                              <td className="px-3 py-2">{formatTime(activity.timestamp)}</td>
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => handleShowSessionFromActivity(session.sessionId)}
                                  className="text-blue-600 hover:underline font-mono text-xs truncate max-w-[120px] block"
                                  title={session.sessionId}
                                >
                                  {session.sessionId.length > 16 ? session.sessionId.substring(0, 16) + '…' : session.sessionId}
                                </button>
                              </td>
                              <td className="px-3 py-2 max-w-[200px] truncate" title={activity.path}>{activity.path || '-'}</td>
                              <td className="px-3 py-2">
                                {activity.metadata && Object.keys(activity.metadata).length > 0
                                  ? Object.entries(activity.metadata).map(([k, v]) => `${k}: ${v}`).join(', ')
                                  : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default VisitorAnalysisPage;
